/**
 * 端末のコンパス方位（停車中の向き補助用）。
 * - iOS: DeviceOrientationEvent.requestPermission() をユーザー操作内で呼ぶ必要がある。
 *        方位は webkitCompassHeading（北=0, 時計回り）。
 * - Android: deviceorientationabsolute の alpha から算出。
 * 走行中は GPS の heading を優先し、これは停車中の補助にだけ使う想定。
 */
type IOSOrientationEvent = DeviceOrientationEvent & { webkitCompassHeading?: number };
type PermissionFn = () => Promise<'granted' | 'denied'>;

export class Compass {
  heading: number | null = null;
  status: 'idle' | 'granted' | 'denied' | 'unsupported' = 'idle';
  private handler = (e: Event) => {
    const ev = e as IOSOrientationEvent;
    if (typeof ev.webkitCompassHeading === 'number') {
      this.heading = ev.webkitCompassHeading;
    } else if (ev.absolute && ev.alpha != null) {
      this.heading = (360 - ev.alpha) % 360;
    }
  };

  /** ユーザー操作（タップ）のハンドラ内から呼ぶこと */
  async start(): Promise<void> {
    if (typeof DeviceOrientationEvent === 'undefined') {
      this.status = 'unsupported';
      return;
    }
    const req = (DeviceOrientationEvent as unknown as { requestPermission?: PermissionFn }).requestPermission;
    if (typeof req === 'function') {
      try {
        this.status = (await req()) === 'granted' ? 'granted' : 'denied';
      } catch {
        this.status = 'denied';
      }
      if (this.status !== 'granted') return;
      window.addEventListener('deviceorientation', this.handler);
    } else {
      this.status = 'granted';
      window.addEventListener('deviceorientationabsolute', this.handler);
      window.addEventListener('deviceorientation', this.handler);
    }
  }

  stop(): void {
    window.removeEventListener('deviceorientation', this.handler);
    window.removeEventListener('deviceorientationabsolute', this.handler);
  }
}
